import memoizeOne from "memoize-one";
import { IStore } from "reducers";
import { getSnapshot } from "../mutate";
import { TargetGroupWithTargets } from "components/product-collection/footer/types";
import { TARGETS_ID, TARGET_GROUPS_ID } from "constants/product-collection";
import { MetricsKind } from "helpers/metrics/types";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";

const getTargetGroupsWithTargetsRaw = (state: IStore, poi?: string): TargetGroupWithTargets[] => {
    const targetGroupsSnapshot = getSnapshot(state, TARGET_GROUPS_ID, poi)?.toJS() || [];
    const targetsSnapshot = getSnapshot(state, TARGETS_ID, poi)?.toJS() || {};

    // Nest Targets into Target Group
    const targetGroupsWithTargets = targetGroupsSnapshot.map((targetGroup) => {
        const targets = (targetsSnapshot[targetGroup.struct_value?.id ?? targetGroup.id]?.list.values ?? []).map(
            (target) => ({
                // TODO handle the mutation of targets_groups correctly without the need of checking for struct_value
                ...(target.struct_value ?? target),
            }),
        );

        return {
            // TODO handle the mutation of targets_groups correctly without the need of checking for struct_value
            ...(targetGroup.struct_value ?? targetGroup),
            targets,
        };
    });

    return targetGroupsWithTargets;
};

/**
 * Equality function that will invalidate memoized result if either:
 * `target_groups` current and prev object differ
 * OR
 * `targets` current and prev object differ
 * @param args
 * @param prevArgs
 * @returns
 */
const getTargetGroupsAndTargetsEqualityFn = (
    args: Parameters<typeof getTargetGroupsWithTargetsRaw>,
    prevArgs: Parameters<typeof getTargetGroupsWithTargetsRaw>,
) => {
    const targetGroups = getSnapshot(args[0], TARGET_GROUPS_ID, args[1]);
    const prevTargetGroups = getSnapshot(prevArgs[0], TARGET_GROUPS_ID, prevArgs[1]);

    const targets = getSnapshot(args[0], TARGETS_ID, args[1]);
    const prevTargets = getSnapshot(prevArgs[0], TARGETS_ID, prevArgs[1]);

    return targetGroups === prevTargetGroups && targets === prevTargets;
};

export const getTargetGroupsWithTargets = memoizeOne(
    getTargetGroupsWithTargetsRaw,
    getTargetGroupsAndTargetsEqualityFn,
);

/**
 * Utility function used to iterate through the active metrics and return the first metric kind found
 * @param dimensionsPanelReduxState
 * @returns MetricsKind of the first metric
 */
export const getFirstSelectedTargetMetricKind = (
    dimensionsPanelReduxState: DimensionsPanelReduxState,
): MetricsKind | null => {
    let firstFoundMetric = null;

    for (const metricValue of dimensionsPanelReduxState.metrics.values) {
        if (metricValue.showValue || metricValue.showSubtotals || metricValue.showTotals) {
            firstFoundMetric = metricValue;
            break;
        }
    }

    return firstFoundMetric?.kind ?? null;
};
